import * as Types from '@aeppic/types'

export type ValidationSelector = { kind: 'RULE' | 'SET' | 'KEY' | 'GROUP'; value: string }
export type ValidationRequirements = {
  mode?: 'INHERIT' | 'REQUIRE' | 'BYPASS'
  require?: ValidationSelector[]
  exclude?: ValidationSelector[]
}
export type ResolvedValidations = {
  basic: boolean
  rules: Types.Document[]
  form: Types.Document
  basicReplacementRuleIds: string[]
  errors: string[]
}

const list = (value: any): any[] => Array.isArray(value) ? value : []
const basicKey = (data: any) => ['basic', 'aeppic:basic'].includes(data?.key || data?.name)
const id = (value: any) => typeof value === 'string' ? value : value?.id

/** Resolve only form-opted-in rules. A selector never searches unrelated applications. */
export async function resolveValidationRequirements(api: any, target: Types.Document, category: string,
  requirements: ValidationRequirements = {}, defaultBlocking = true): Promise<ResolvedValidations> {
  const stored = await api.getFormForDocument(target)
  const latest = await api.get(target.f.id)
  const form = latest?.data?.validationVersionPolicy === 'DOCUMENT_VERSION'
    ? stored.document
    : latest || stored.document
  const result: ResolvedValidations = { basic: false, rules: [], form, basicReplacementRuleIds: [], errors: [] }
  if (requirements.mode === 'BYPASS') return result

  const configurationFields = ['validationSets', 'validationRules', 'basicValidationMode']
  const hasValidationConfiguration = configurationFields.some(field =>
    Object.prototype.hasOwnProperty.call(form.data || {}, field))
  if (!hasValidationConfiguration) return result
  const sets: Types.Document[] = []
  const rules = new Map<string, Types.Document>()
  for (const ref of form.data?.validationSets || []) {
    const set = await api.get(ref)
    if (!set) { result.errors.push('Validation set not found: ' + id(ref)); continue }
    if (set.data?.enabled === false) continue
    sets.push(set)
    for (const ruleRef of set.data?.validations || []) {
      const rule = await api.get(ruleRef)
      if (!rule) { result.errors.push('Validation rule not found: ' + id(ruleRef)); continue }
      if (rule.data?.enabled !== false && rule.data?.disabled !== true) rules.set(rule.id, rule)
    }
  }
  for (const ref of form.data?.validationRules || []) {
    const rule = await api.get(ref)
    if (!rule) { result.errors.push('Validation rule not found: ' + id(ref)); continue }
    if (rule.data?.enabled !== false && rule.data?.disabled !== true) rules.set(rule.id, rule)
  }
  const selected = new Set<string>()
  const members = (set: Types.Document) => list(set.data?.validations).map(id).filter(key => rules.has(key))
  const match = (selector: ValidationSelector): string[] => {
    if (selector.kind === 'RULE') return rules.has(selector.value) ? [selector.value] : []
    if (selector.kind === 'SET') return sets.filter(s => s.id === selector.value).flatMap(members)
    if (selector.kind === 'KEY') return [
      ...[...rules.values()].filter(r => r.data?.key === selector.value).map(r => r.id),
      ...sets.filter(s => (s.data?.key || s.data?.name) === selector.value).flatMap(members),
    ]
    if (selector.kind === 'GROUP') return [
      ...[...rules.values()].filter(r => list(r.data?.groups).some(g => id(g) === selector.value)).map(r => r.id),
      ...sets.filter(s => list(s.data?.groups).some(g => id(g) === selector.value)).flatMap(members),
    ]
    return []
  }
  const basicMode = form.data?.basicValidationMode || 'DEFAULT'
  if (defaultBlocking && requirements.mode !== 'REQUIRE') {
    // Only upgraded form definitions acquire an implicit basic requirement.
    // Saving incomplete documents remains allowed; consumers enforce this policy.
    result.basic = Object.prototype.hasOwnProperty.call(form.data || {}, 'basicValidationMode')
      && basicMode !== 'REPLACE'
    for (const set of sets) {
      if (list(set.data?.categories).includes(category)
        || basicKey(set.data)) {
        for (const key of members(set)) selected.add(key)
      }
    }
  }
  const explicit = new Set<string>()
  for (const selector of requirements.require || []) {
    if (selector.kind === 'KEY' && selector.value === 'aeppic:basic') {
      result.basic = basicMode !== 'REPLACE'
      const basicRules = sets.filter(s => basicKey(s.data)).flatMap(members)
      for (const key of basicRules) { selected.add(key); explicit.add(key) }
      continue
    }
    const found = match(selector)
    if (!found.length) result.errors.push('Required validation selector has no applicable rules: ' + selector.value)
    for (const key of found) { selected.add(key); explicit.add(key) }
  }
  for (const selector of requirements.exclude || []) {
    if (selector.kind === 'KEY' && selector.value === 'aeppic:basic') {
      if ((requirements.require || []).some(s => s.kind === 'KEY' && s.value === 'aeppic:basic')) result.errors.push('Basic validation is both required and excluded.')
      result.basic = false
      for (const set of sets.filter(s => basicKey(s.data))) {
        for (const key of members(set)) { if (explicit.has(key)) result.errors.push('Validation is both required and excluded: ' + key); selected.delete(key) }
      }
      continue
    }
    for (const key of match(selector)) {
      if (explicit.has(key)) result.errors.push('Validation is both required and excluded: ' + key)
      selected.delete(key)
    }
  }
  if (basicMode === 'REPLACE') {
    result.basicReplacementRuleIds = sets.filter(s => basicKey(s.data)).flatMap(members).filter(key => selected.has(key))
  }
  result.rules = [...selected].map(key => rules.get(key)!)
  if (basicMode === 'REPLACE' && defaultBlocking && requirements.mode !== 'REQUIRE'
    && !(requirements.exclude || []).some(selector => selector.kind === 'KEY' && selector.value === 'aeppic:basic')
    && !sets.some(s => basicKey(s.data) && members(s).length)) {
    result.errors.push('Replacement basic validation requires a non-empty basic validation set.')
  }
  return result
}
